/*
 * Carry the visitor's campaign (utm_*) params to the app. Pages are prerendered, so
 * links to app.singulate.com ship without them; instead the href is completed at click
 * time, just before the browser follows it. That works for every such link on the site
 * (no per-component wiring) and with the client-side router, which never re-renders them.
 *
 * The first utm_* seen in a tab is kept in sessionStorage, so a visitor who lands on a
 * campaign page and clicks through to another before signing up still carries them.
 * Params already on the link (code=...) are never replaced.
 */
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id']
const STORE_KEY = 'sg_utm'
const APP_HOST = 'app.singulate.com'

function fromUrl(): Record<string, string> {
  const out: Record<string, string> = {}
  const q = new URLSearchParams(window.location.search)
  for (const k of UTM_KEYS) {
    const v = q.get(k)
    if (v) out[k] = v.slice(0, 200)
  }
  return out
}

function stored(): Record<string, string> {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || '{}') as Record<string, string>
  } catch {
    return {}
  }
}

/** Remember the first campaign params of this tab; the current URL's win over stored ones when both exist. */
export function captureUtms() {
  const current = fromUrl()
  if (!Object.keys(current).length) return
  try {
    if (!window.sessionStorage.getItem(STORE_KEY)) window.sessionStorage.setItem(STORE_KEY, JSON.stringify(current))
  } catch {
    /* storage can be blocked; the current URL still works for this page */
  }
}

function decorate(a: HTMLAnchorElement) {
  let url: URL
  try {
    url = new URL(a.href, window.location.href)
  } catch {
    return
  }
  if (url.hostname !== APP_HOST) return
  const utms = { ...stored(), ...fromUrl() }
  let changed = false
  for (const [k, v] of Object.entries(utms)) {
    if (UTM_KEYS.includes(k) && v && !url.searchParams.has(k)) {
      url.searchParams.set(k, v)
      changed = true
    }
  }
  if (changed) a.href = url.toString()
}

export function initUtmPassthrough() {
  try {
    captureUtms()
    // pointerdown covers new-tab and middle clicks, click covers keyboard activation
    const handler = (e: Event) => {
      const a = (e.target as Element | null)?.closest?.('a[href]')
      if (a instanceof HTMLAnchorElement) decorate(a)
    }
    document.addEventListener('pointerdown', handler, true)
    document.addEventListener('click', handler, true)
    document.addEventListener('keydown', handler, true)
  } catch {
    /* never break the page for tracking params */
  }
}
